import { useState } from "react"
import { Loader2 } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  useGetAccountReferralsQuery,
  useAccount,
  useRafReward,
} from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import { useResendRewardMutation } from "@apis/rewards/useRewards"
import { DataTable } from "@components/DataTable/DataTable"
import { useHasPermissions } from "@hooks/useHasPermissions"
import { referralColumns } from "./tableColumnSchema"
import { Button } from "@components/ui/button"
import {
  ReissueRewardForm,
  type TReissueRewardForm,
} from "../Rewards/ReissueRewardForm"

type TReferralDetailProps = {
  account_id: string
}

export function ReferralDetail({ account_id }: TReferralDetailProps) {
  const queryClient = useQueryClient()
  const { canResendReward } = useHasPermissions()
  const accountQuery = useAccount(account_id)
  const resendRewardMutation = useResendRewardMutation()
  const accountReferralsQuery = useGetAccountReferralsQuery(account_id)
  const rafRewardQuery = useRafReward(account_id)
  const [showResendReward, setShowResendReward] = useState(false)

  if (accountReferralsQuery.isPending || accountQuery.isPending) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin" />
      </div>
    )
  }

  if (accountReferralsQuery.isError || accountQuery.isError) {
    return (
      <div className="tw:text-red-500">
        Error loading referral data. Please try again.
      </div>
    )
  }

  const reward = rafRewardQuery.data

  const onSubmitResendReward = (values: TReissueRewardForm) => {
    if (!reward) return
    resendRewardMutation.mutate(
      {
        reward_guid: reward.reward_guid,
        data: {
          service_zip_code: accountQuery.data.service_zip_code,
          fulfillment_channel: values.channel_code,
        },
      },
      {
        onSuccess: () => {
          toast.success("Successfully resent reward!")
          setShowResendReward(false)
        },
        onError: (error) => toast.error(`Error resending reward! ${error}`),
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountRewards(account_id),
          })
        },
      },
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div>
        <p>{accountReferralsQuery.data.description}</p>
      </div>

      <div className="tw:flex tw:flex-col tw:gap-4">
        <h3 className="tw:mb-0 tw:font-semibold">Pending</h3>
        <DataTable
          columns={referralColumns}
          data={accountReferralsQuery.data.referrals?.accrued?.details || []}
          noResultsMessage="No pending referrals"
        />
      </div>

      <div className="tw:flex tw:flex-col tw:gap-4">
        <h3 className="tw:mb-0 tw:font-semibold">In Process</h3>
        <DataTable
          columns={referralColumns}
          data={accountReferralsQuery.data.referrals?.earned?.details || []}
          noResultsMessage="No in-process referrals"
        />
      </div>

      <div className="tw:flex tw:flex-col tw:gap-4">
        <h3 className="tw:mb-0 tw:font-semibold">Completed</h3>
        <DataTable
          columns={referralColumns}
          data={accountReferralsQuery.data.referrals?.settled?.details || []}
          noResultsMessage="No completed referrals"
        />
      </div>

      {reward?.can_resend && canResendReward && (
        <div className="tw:flex tw:flex-col tw:items-start tw:gap-3">
          <p>
            This reward is eligible to be resent. Click 'Resend' below to resend
            for the customer.
          </p>
          {!showResendReward ? (
            <Button onClick={() => setShowResendReward(true)}>
              Resend {reward.reward_amount}
            </Button>
          ) : (
            <ReissueRewardForm
              onCancel={setShowResendReward}
              reward={reward}
              onSubmit={onSubmitResendReward}
              isPending={resendRewardMutation.isPending}
            />
          )}
        </div>
      )}
    </div>
  )
}
